import { Box, Container } from '@mui/material'
import { CarrierVisitStatus } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { StoppageForm } from '@operations/features/stoppage/components/molecules/StoppageForm'
import { StoppageFormHeader } from '@operations/features/stoppage/components/organisms/StoppageFormHeader'
import { useStoppageMessageHandler } from '@operations/features/stoppage/hooks/useStoppageMessageHandler'
import { StoppageFormData } from '@operations/stores/StoppageStore'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'

export const StoppageFormPage = withAuthenticationRequired(
  observer(() => {
    const { stoppageStore, stoppageReasonStore, carrierVisitStore, appStore } = useOperationsStore()
    const navigate = useNavigate()
    const { getExceptionMessage, getSuccessMessage } = useStoppageMessageHandler()
    const theme = useMinimalsTheme()
    const { id, stoppageId } = useParams<{ id: string; stoppageId: string }>()
    const formId = 'stoppageForm'

    const visitId = Number(id)
    const stoppageIdNumber = Number(stoppageId)

    useAsyncFetch(async () => {
      await Promise.all([
        carrierVisitStore.loadOne(visitId),
        stoppageStore.fetchOneById(stoppageIdNumber),
        stoppageStore.loadEquipments(visitId),
        stoppageReasonStore.fetchStoppageReasons(),
      ])

      stoppageStore.setSelectedVisitId(visitId)
    }, [stoppageIdNumber, stoppageReasonStore, stoppageStore, carrierVisitStore, visitId])

    const visit = computed(() => carrierVisitStore.items.find(v => v.id === visitId)).get()

    const handleSubmit = async (formData: StoppageFormData) => {
      try {
        await stoppageStore.onSubmit(formData)
        appStore.setShowAlert('success', getSuccessMessage(!!formData.id))
        navigate(
          `${PATHS.visits.vesselVisits.url}/${visitId}/${PATHS.visits.vesselVisits.stoppages.url}`,
        )
      } catch (error) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          minHeight: '100%',
        }}
      >
        <StoppageFormHeader visit={visit} stoppageId={stoppageIdNumber} formId={formId} />

        <Container sx={{ paddingY: 2 }}>
          <StoppageForm
            formId={formId}
            vesselVisitId={visitId}
            stoppage={stoppageStore.items.find(x => x.id === stoppageIdNumber)}
            stoppageReasons={stoppageReasonStore.items}
            equipments={stoppageStore.cranes}
            isVesselDeparted={
              visit?.status === CarrierVisitStatus.Departed ||
              visit?.status === CarrierVisitStatus.Completed
            }
            onSubmit={handleSubmit}
          />
        </Container>
      </Box>
    )
  }),
)
